import fetcher, { makeFetcher } from '@sls-react/api/fetcher';
import { RailsUser } from '@sls-react/typings/services/rails/user';
import {
  RailsItem,
  RailsShipping,
} from '@sls-react/typings/services/rails/item';

type ItemId = number | string;

export const fetchItemById = makeFetcher<
  RailsItem,
  [itemId: ItemId, track?: boolean]
>((itemId, track = false) => [
  `/v1/items/${itemId}`,
  {
    params: {
      track,
    },
  },
]);

export const fetchFavoriters = makeFetcher<
  Pick<RailsUser, 'id' | 'username' | 'avatar_url' | 'following'>[],
  [itemId: ItemId]
>(itemId => `/v1/items/${itemId}/favoriters`);

export const favoriteItem = makeFetcher<
  Pick<RailsItem, 'id' | 'favorite' | 'favoriters_count'>[],
  [itemId: ItemId]
>(itemId => [`/v1/items/${itemId}/favorite`, { method: 'post' }]);

export const unfavoriteItem = makeFetcher<
  Pick<RailsItem, 'id' | 'favorite' | 'favoriters_count'>[],
  [itemId: ItemId]
>(itemId => [`/v1/items/${itemId}/favorite`, { method: 'delete' }]);

export const postReportItem = makeFetcher<
  never,
  [itemId: ItemId, reason: string, message: string]
>((itemId, reason: string, message: string) => [
  `/v1/items/${itemId}/report`,
  {
    body: {
      reason,
      message,
    },
    method: 'post',
  },
]);

export const postRemoveItem = makeFetcher<
  RailsItem,
  [itemId: ItemId, reason: string, message: string]
>((itemId, reason: string, message: string) => [
  `/v1/items/${itemId}/remove`,
  {
    body: {
      reason_removed: reason,
      message,
    },
    method: 'post',
  },
]);

export const deleteDiscardDraft = makeFetcher<never, [itemId: ItemId]>(
  itemId => [
    `/v1/items/${itemId}/discard_draft`,
    {
      method: 'delete',
    },
  ],
);

export const postTakeDownItem = makeFetcher<never, [itemId: ItemId]>(itemId => [
  `/v1/items/${itemId}/take_down`,
  {
    method: 'post',
  },
]);

export const postPublishItem = makeFetcher<RailsItem, [itemId: ItemId]>(
  itemId => [
    `/v1/items/${itemId}/publish`,
    {
      method: 'post',
    },
  ],
);

export const addItemToSale = makeFetcher<
  never,
  [itemId: ItemId, saleId: number | string]
>((itemId, saleId) => [
  `/v1/items/${itemId}/sales/${saleId}`,
  {
    method: 'put',
  },
]);

export const removeItemFromSale = makeFetcher<
  never,
  [itemId: ItemId, saleId: number | string]
>((itemId, saleId) => [
  `/v1/items/${itemId}/sales/${saleId}`,
  {
    method: 'delete',
  },
]);

export const unreportItem = makeFetcher<never, [itemId: ItemId]>(itemId => [
  `/v1/items/${itemId}/unreport`,
  {
    method: 'post',
  },
]);

export const itemRemoveGmc = makeFetcher<never, [itemId: ItemId]>(itemId => [
  `/v1/items/${itemId}/remove_gmc`,
  {
    method: 'post',
  },
]);

export const fetchItemShipping = makeFetcher<
  RailsShipping,
  [
    itemId: ItemId,
    params: { zip: string; country: string } | { ship_to_address_id: number },
  ]
>(
  (
    itemId,
    params: { zip: string; country: string } | { ship_to_address_id: number },
  ) => [
    `/v1/items/${itemId}/shipping`,
    {
      params,
    },
  ],
);

export const postBumpItem = (itemId: ItemId) =>
  fetcher<RailsItem>(`/v1/items/${itemId}/bump`, {
    method: 'post',
  });

export const priceDropItem = makeFetcher<
  RailsItem,
  [itemId: ItemId, newPrice: number | string]
>((itemId, newPrice) => [
  `/v1/items/${itemId}/price_drop`,
  {
    method: 'put',
    body: {
      price: newPrice,
    },
  },
]);
